import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trackChunkLoadError, trackError } from "@/utils/errorTracking";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    
    // CRITICAL FIX #35-38: Better error categorization
    const isChunkError = error.message?.includes('Failed to fetch dynamically imported module') ||
                        error.message?.includes('Importing a module script failed') ||
                        error.message?.includes('chunk');
    const isAuthError = error.message?.includes('JWT') || 
                       error.message?.includes('session') ||
                       error.message?.includes('Unauthorized');
    
    if (isChunkError) {
      trackChunkLoadError(error);
    } else {
      trackError(error, { errorInfo, isAuthError });
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      const isChunkError = this.state.error?.message?.includes('dynamically imported module') ||
                          this.state.error?.message?.includes('module script failed') ||
                          this.state.error?.message?.includes('chunk');
      const isAuthError = this.state.error?.message?.includes('JWT') || 
                         this.state.error?.message?.includes('session') ||
                         this.state.error?.message?.includes('Unauthorized');

      // CRITICAL FIX #35: Chunk error with clear instructions
      if (isChunkError) {
        return (
          <div className="min-h-screen flex items-center justify-center p-6 bg-background">
            <Card className="max-w-lg w-full">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <RefreshCw className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle>Update Available</CardTitle>
                    <CardDescription>A new version of the app is ready</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  We've released an update with new features and improvements. Please refresh your browser to get the latest version.
                </p>
                <Button onClick={() => window.location.reload()} className="w-full" size="lg">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh Now
                </Button>
              </CardContent>
            </Card>
          </div>
        );
      }

      // CRITICAL FIX #36: Auth error with guidance
      if (isAuthError) {
        return (
          <div className="min-h-screen flex items-center justify-center p-6 bg-background">
            <Card className="max-w-lg w-full">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-yellow-500/10">
                    <AlertTriangle className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <CardTitle>Session Expired</CardTitle>
                    <CardDescription>Your login session has ended</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  For your security, we've logged you out after a period of inactivity. Please refresh and log in again to continue.
                </p>
                <div className="flex gap-2">
                  <Button onClick={() => window.location.reload()} className="flex-1" size="lg">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh & Login
                  </Button>
                  <Button 
                    onClick={() => window.location.href = '/'} 
                    variant="outline" 
                    className="flex-1"
                    size="lg"
                  >
                    <Home className="mr-2 h-4 w-4" />
                    Go Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      }

      // CRITICAL FIX #38: Default error with helpful actions
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-background">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-destructive/10">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <CardTitle>Something Went Wrong</CardTitle>
                  <CardDescription>The application encountered an unexpected error</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-muted">
                <p className="text-xs font-semibold mb-2 text-muted-foreground">Error Details:</p>
                <p className="text-sm font-mono text-foreground">
                  {this.state.error?.message || "Unknown error occurred"}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                This might be a temporary issue. Try refreshing the page or returning to the dashboard.
              </p>
              <div className="flex flex-col gap-2">
                <Button onClick={() => window.location.reload()} className="w-full" size="lg">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh Page
                </Button>
                <div className="flex gap-2">
                  <Button onClick={this.handleReset} variant="outline" className="flex-1">
                    Try Again
                  </Button>
                  <Button 
                    onClick={() => window.location.href = '/dashboard'} 
                    variant="outline" 
                    className="flex-1"
                  >
                    <Home className="mr-2 h-4 w-4" />
                    Dashboard
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
